import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap transition-colors disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 rounded-md disabled:opacity-50",
        destructive:
          "rounded-md bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "rounded-md border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        ghost: "rounded-md hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "rounded-md text-primary underline-offset-4 hover:underline",
        // public-primary
        primary:
          "bg-primary-500 text-white hover:bg-primary-700 active:bg-primary-800 disabled:bg-secondary-200 disabled:text-gray-400 rounded-[50px]",
        secondary:
          "bg-secondary-600 text-white hover:bg-secondary-700 active:bg-secondary-800 disabled:bg-secondary-200 disabled:text-gray-400 rounded-[50px]",
        tertiary:
          "bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:bg-secondary-200 disabled:text-gray-400 rounded-[50px]",
        line: "text-gray-700 border border-gray-200 hover:bg-gray-50 active:bg-gray-100 disabled:bg-secondary-200 disabled:text-gray-400 disabled:border-none rounded-[50px]",
        white_line:
          "text-white border rounded-[50px] border-white hover:bg-white hover:text-gray-700 active:bg-gray-50 active:border-white disabled:bg-secondary-200 disabled:text-gray-400 disabled:border-none",
        input:
          "bg-secondary-600 text-white rounded-[10px] body-md-bold disabled:bg-secondary-200 disabled:text-gray-400",
        text_icon: "bg-transparent text-gray-700 hover:border-gray-700 hover:border-b",
        text: "bg-transparent h-8 hover:border-b",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 body-sm-regular px-3",
        md: "pc:h-[42px] h-[46px] px-5 body-md-bold",
        lg: "h-[54px] tb:h-14 pc:h-14 tb:px-[50px] px-5 body-lg-bold ",
        input: "px-4 tb:px-9 h-[46px] tb:h-[52px]  min-w-20 tb:min-w-[100px]",
        icon: "size-9",
        text_lg: "h-fit px-[2px] body-md-bold",
        text_sm: "h-fit px-[2px] body-md-regular",
      },
    },
    compoundVariants: [
      // text_icon variant는 특별한 사이즈 체계 사용
      {
        variant: "text_icon",
        size: "sm",
        class: "body-sm-regular h-fit gap-1 px-0 [&_svg]:size-4", // text_icon small
      },
      {
        variant: "text_icon",
        size: "lg",
        class: "body-md-regular h-fit gap-[6px] px-0 [&_svg]:size-5", // text_icon large
      },
      {
        variant: "text",
        size: "text_sm",
        class:
          "body-md-regular h-fit text-gray-500 border-b border-gray-500 hover:border-gray-400 hover:text-gray-400 disabled:text-gray-300 disabled:border-gray-300 px-0", // text small
      },
      {
        variant: "text",
        size: "text_lg",
        class: "body-md-bold h-fit px-[2px] text-primary-500 hover:border-primary-500 disabled:text-gray-300", // text large
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
